import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Moment } from 'moment';
import { SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { enLocales, frLocales } from './date-range.locales';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { WCAGLabelDirective } from '@shared/directives/wcag-label.directive';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButton } from 'primeng/selectbutton';
import { FixSelectButtonAriaDirective } from '@shared/directives/date-range-selectable-fix.directive';

export interface IDateRange {
  dateFrom: Date;
  dateTo: Date;
}

@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [DatePipe],
  imports: [CommonModule, FormsModule, WCAGLabelDirective, DatePickerModule, FixSelectButtonAriaDirective, SelectButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComponent implements OnInit, OnDestroy, OnChanges {

  public locales = {
    en: enLocales,
    fr: frLocales,
  };
  selectedDate: Date;
  @Output() datesChanged = new EventEmitter<IDateRange>();

  @Input() dateFrom: Date;
  @Input() dateTo: Date;
  @Input() lang;
  @Input() placeholder: string;

  dateFromUTC: Date;
  dateToUTC: Date;

  private _destroy$ = new Subject<any>();

  dateRangeLabel: string;
  calendarOptionsOpened = false;
  isDateChanged = false;
  dateOptions: SelectItem[];
  selectedDateOption: any;

  constructor(
    public datePipe: DatePipe,
    private eRef: ElementRef,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
    private auth: AuthService
  ) {
    this.auth.translationLoading$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.setDateRangeLabel();
        this.setDateOptions();
      });

    if (!this.lang) {
      this.translateService.onLangChange
        .pipe(
          takeUntil(this._destroy$),
        )
        .subscribe(
        () => {
          this.lang = this.translateService.currentLang;
          this.setDateRangeLabel();
          this.setDateOptions();
        },
      );
    }

  }

  @HostListener('document:click', ['$event'])

  clickout(event: MouseEvent) {
    const target = event.target as HTMLElement;
  
    const clickedInsideComponent = this.eRef.nativeElement.contains(target);
    const clickedInsideCalendarPopup = this.isInsidePrimeNgCalendarPopup(target);
  
    // console.log('clickedInsideComponent:', clickedInsideComponent);
    // console.log('clickedInsideCalendarPopup:', clickedInsideCalendarPopup);
  
    if (!clickedInsideComponent && !clickedInsideCalendarPopup) {
      this.calendarOptionsOpened = false;
    }
  }

  private isInsidePrimeNgCalendarPopup(target: HTMLElement): boolean {
    while (target) {
      if (
        target.classList &&
        Array.from(target.classList).some(cls =>
          cls.includes('p-datepicker') ||
          cls.includes('p-calendar') ||
          cls.includes('p-link') ||
          cls.includes('p-yearpicker') ||
          cls.includes('p-monthpicker') ||
          cls.includes('p-month') ||
          cls.includes('p-year')
        )
      ) {
        return true;
      }
  
      // Check for known tag + attributes PrimeNG uses in popup calendar
      if (target.tagName === 'DIV' && target.getAttribute('role') === 'dialog') {
        return true;
      }
  
      target = target.parentElement;
    }
  
    return false;
  }

  // private isInsidePrimeNgCalendarPopup(target: HTMLElement): boolean {
  //   while (target) {
  //     if (target.classList && Array.from(target.classList).some(cls =>
  //       cls.startsWith('p-datepicker') || cls.includes('p-calendar')
  //     )) {
  //       return true;
  //     }
  //     target = target.parentElement;
  //   }
  //   return false;
  // }


  ngOnInit() {
    this.dateFromUTC = this.dateFrom ? moment(this.dateFrom).toDate() : new Date();
    this.dateToUTC = this.dateTo ? moment(this.dateTo).toDate() : new Date(); 
    this.lang = this.lang || this.translateService.currentLang;
    this.setDateRangeLabel();
    this.setDateOptions();
  }

  toggleCalendar() {
    this.calendarOptionsOpened = !this.calendarOptionsOpened;
    if (this.isDateChanged) {
      this.updateDatesInDashboardContext();
    }
  }

  setDateRangeLabel() {
    moment.locale(this.translateService.currentLang);
    if (this.dateFrom && this.dateTo) {
      this.dateRangeLabel =
        `${moment(this.dateFrom).format('DD MMM YYYY')}
      - ${moment(this.dateTo).format('DD MMM YYYY')}`;
    } else {
      this.dateRangeLabel = this.translateService.instant(this.placeholder || 'COMMON.calendar.dateRange');
    }
    this.cd.markForCheck();
  }

  onChangedDateOption(event:any) {
    this.selectedDateOption = event?.value;
    if (DateOption.THIS_MONTH === this.selectedDateOption) {
      this.setDatesOnThisMonth();
    } else if (DateOption.THIS_YEAR === this.selectedDateOption) {
      this.setDatesOnThisYear();
    } else if (DateOption.LAST_MONTH === this.selectedDateOption) {
      this.setDatesOnLastMonth();
    } else if (DateOption.LAST_YEAR === this.selectedDateOption) {
      this.setDatesOnLastYear();
    }
    this.customCalendarDatesChanged();
    this.isDateChanged = true;
  }

  customCalendarDatesChanged(source?: 'from' | 'to') {
    this.dateFrom = moment(this.dateFromUTC).startOf('day').toDate();
    this.dateTo = moment(this.dateToUTC).endOf('day').toDate();
    this.setDateRangeLabel();
    this.isDateChanged = true;

    this.updateDatesInDashboardContext();
    if (source === 'to') {
      this.calendarOptionsOpened = false;
    }
  }

  onPickerDateChange(source: 'from' | 'to', value: Date): void {
    if (!value) {
      return;
    }

    if (source === 'from') {
      this.dateFromUTC = value;
      if (this.dateToUTC && this.dateToUTC < this.dateFromUTC) {
        this.dateToUTC = new Date(this.dateFromUTC);
      }
    } else {
      this.dateToUTC = value;
    }

    this.customCalendarDatesChanged(source);

  }

  updateDatesInDashboardContext(): void {
    if (this.dateFrom && this.dateTo) {
      this.datesChanged.emit({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      });
      this.isDateChanged = false;
    }
  }

  private toUTC(date: Date): Date {
    return moment.utc(moment(date).format('YYYY-MM-DD')).toDate();
  }

  setDatesOnThisMonth() {
    this.dateFromUTC = (moment().startOf('month').toDate());
    this.dateToUTC = (moment().endOf('month').startOf('day').toDate())
  }
  setDatesOnThisYear() {
    this.dateFromUTC = (moment().startOf('year').toDate());
    this.dateToUTC = (moment().endOf('year').startOf('day').toDate());
  }

  setDatesOnLastMonth() {
    this.dateFromUTC = (moment().subtract(1, 'months').startOf('month').toDate());
    this.dateToUTC = (moment().subtract(1, 'months').endOf('month').startOf('day').toDate());
  }

  setDatesOnLastYear() {
    this.dateFromUTC = (moment().subtract(1, 'years').startOf('year').toDate());
    this.dateToUTC = (moment().subtract(1, 'years').endOf('year').startOf('day').toDate());
  }

  setDateOptions() {
    this.dateOptions = [
      {
        label: this.translateService.instant('COMMON.calendar.thisMonthText'),
        value: DateOption.THIS_MONTH,
      },
      {
        label: this.translateService.instant('COMMON.calendar.thisYearText'),
        value: DateOption.THIS_YEAR,
      },
      {
        label: this.translateService.instant('COMMON.calendar.lastMonthText'),
        value: DateOption.LAST_MONTH,
      },
      {
        label: this.translateService.instant('COMMON.calendar.lastYearText'),
        value: DateOption.LAST_YEAR,
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dateFrom || changes.dateTo) {
    this.dateFromUTC = this.dateFrom ? moment(this.dateFrom).toDate() : new Date();
    this.dateToUTC = this.dateTo ? moment(this.dateTo).toDate() : new Date();
  }

  this.setDateRangeLabel();
  }

  keyPressed(event, method: string, ignoredKeys: string[] = []): void {
    if (!this.calendarOptionsOpened) {
      if (!(event.key && ignoredKeys.includes(event.key))) {
        this[method]();
      }
    }
  }

  get minDate() {
    return typeof this.dateFrom === 'string' ? new Date(this.dateFrom) : this.dateFrom;
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.calendarOptionsOpened) {
      if (event.key === 'Escape') {
        this.toggleCalendar();
        (document.getElementsByClassName('hotspot-area')[0] as HTMLElement).focus();
      }
    }

  }

  private toDate(d: Moment): Date {
    return new Date(d.year(), d.month(), d.date(), d.hours(), d.minutes(), d.seconds());
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

}

enum DateOption {
  THIS_MONTH,
  THIS_YEAR,
  LAST_MONTH,
  LAST_YEAR,
}

class UTCDate {
  private boundDateBacker: Date;
  constructor(
    private dateInstance: Date,
    localToUtc = false,
  ) {
    this.boundDateBacker = localToUtc ? this.localToUtc(dateInstance) : this.utcToLocal(dateInstance);
  }

  public get boundDate(): Date {
    return this.boundDateBacker;
  }
  public set boundDate(value: Date) {
    if (value) {
      this.dateInstance.setTime(this.localToUtc(value).getTime());
      const newTime = value.getTime();
      if (newTime !== this.boundDateBacker.getTime()) {
        this.boundDateBacker.setTime(newTime);
      }
    }
  }

  private localToUtc(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(),  date.getHours(), date.getMinutes(), date.getSeconds()));
  }

  private utcToLocal(date: Date): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),  date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
  }
}
